/**
 * Award-data SSOT — Nurses Award MA000034
 *
 * ═══════════════════════════════════════════════════════════════════════
 * ISOLATION GUARANTEE
 * ═══════════════════════════════════════════════════════════════════════
 *
 * This file encodes **MA000034-specific constants ONLY**. DO NOT add fields
 * from other awards' provisions even if they share field names. Cross-award
 * shape-porting risk per `project_cross_award_shape_porting_risk.md` is
 * mitigated by this isolation. See runbook §7
 * (`docs/operations/runbooks/award-engine-clause-provenance.md`).
 *
 * Nurses is the SOLE SPLIT-shape recall award (in-person cl 19.6 vs
 * electronic cl 19.7). HPSS, Aged Care, and SCHADS are all UNIFIED-shape.
 * DO NOT copy this struct to another award's file — that pattern caused
 * 4-of-4 cross-award shape-porting defects (errata #16 / #19 + Phase 1J/1K/1L).
 *
 * ═══════════════════════════════════════════════════════════════════════
 * SOURCES + INDEXATION
 * ═══════════════════════════════════════════════════════════════════════
 *
 * Primary sources for every value below:
 *   - FWC consolidated MA000034
 *   - FWO Pay Guide G01002984
 *   - FWC determination PR791118 (Nurses Stage 3, ppc 1 Oct 2025)
 *
 * Nurses rate data lives in DB (`award_rate_lines`) and is consumed by the
 * engine via `RateContext`. This file carries the engine's structural
 * RULES (recall, shift classification, classification streams, PH OT
 * multiplier) that are NOT in the DB rate-lines table.
 *
 * AWR 2026 hard deadline for reseed: 22 Jun 2026.
 */

import type { OtRateBands, RecallRules, ShiftCharacterRules } from './_shared';

// ═══════════════════════════════════════════════════════════════════════
// Shift classification rules (cl 20.1)
// ═══════════════════════════════════════════════════════════════════════

/**
 * Cl 20.1 whole-shift afternoon/night classification rules for the
 * Nurses Award MA000034.
 *
 * Phase 1A.3 (Award Engine Integrity 2026-05-16) — first cut, finish-only.
 * Phase 1J MD-A3 (2026-05-16) — night bound tightened from 8 to 7.5 per
 *   cl 20.1(b) "finishing before 7.30 am".
 * Phase Codex Pass A (2026-05-19) — cl 20.1 COMMENCE+FINISH AND-trigger
 *   added per P0-4. Pre-Phase-Codex-A the finish-only shape over-paid
 *   shift loading for 10:00-18:30 (over-paid afternoon) and 17:00-01:00
 *   (over-paid night) — direction OVER-pay, but still a defect. Codex
 *   P1-A: cl 20.1(b) verbatim "finishing BEFORE 7.30 am" is now honoured
 *   as an exclusive bound via `finishStrictlyBefore: true` — 07:30 sharp
 *   finish no longer classified as night.
 *
 * Verbatim cl 20.1 (FWC consolidated MA000034 + primary-source
 * verification PayCat MA000034 summary + FWO Library MA000034 +
 * Williamson Barwick Nurses Award):
 *
 *   "(a) Afternoon shift means any shift commencing not earlier than
 *        12.00 noon and finishing after 6.00 pm on the same day.
 *    (b) Night shift means any shift commencing on or after 6.00 pm
 *        and finishing before 7.30 am on the following day."
 *
 * Both conditions (commence AND finish) must hold:
 *   - Afternoon: commences ≥ 12:00 AND finishes > 18:00 (same day)
 *   - Night: commences ≥ 18:00 AND finishes < 07:30 (next day; strict)
 *
 * Counter-examples now correctly classified as DAY (no shift loading):
 *   - 10:00-18:30 — commences before noon → fails afternoon commence
 *   - 17:00-01:00 — commences before 18:00 → fails night commence
 *   - 12:00-18:00 — finishes at exactly 18:00 (not strictly > 18:00) → DAY
 *   - 22:00-07:30 — finishes at exactly 07:30 (not strictly < 07:30) → DAY
 *
 * Source: FWC consolidated MA000034 cl 20.1(a) + cl 20.1(b)
 *         + FWO library K700647.
 *
 * Note on clause numbering: this file's HISTORICAL Phase-1A docstring
 * referenced "cl 27.2" — that referred to the 2010-vintage award
 * numbering. The current MA000034 (2020 award restructure) places
 * shift definitions in cl 20.1. Codex P1-D (2026-05-19) flagged the
 * cross-version drift; this rebuild standardises on cl 20.1 for new
 * provenance. Pre-existing cl 27.2 references in `shift-classifier.ts`
 * commentary are documentation-only and out of scope for this PR.
 *
 * Verified: Phase Codex Pass A (2026-05-19) statura-regulatory-source-agent.
 * Artifact: docs/operations/clause-verifications/2026-05-19-statura-regulatory-source-shift-classification-primary-source-rebuild.md
 */
export const NURSES_SHIFT_RULES: ShiftCharacterRules = {
  afternoon: {
    commenceAtOrAfterHour: 12, // cl 20.1(a) "commencing not earlier than 12.00 noon"
    finishAfterHour: 18,
    finishAtOrBeforeHour: 24,
  },
  night: {
    commenceAtOrAfterHour: 18, // cl 20.1(b) "commencing on or after 6.00 pm"
    finishAfterHour: 0,
    finishAtOrBeforeHour: 7.5,
    finishStrictlyBefore: true, // cl 20.1(b) verbatim "finishing BEFORE 7.30 am" — exclusive upper bound (Codex P1-A 2026-05-19)
  },
};

/**
 * Nurses EA cl 22.1 finish-time shift windows — DIFFER from the base modern
 * award (cl 20.1) on two points, both primary-source verified 2026-07-15
 * (`reference_medea_park_ea_shift_pay_rulings`; OCR AE530670 defs pp6–7):
 *   • afternoon has NO commencement bound — "a shift finishing between 6.00pm
 *     and midnight" (the base award's `commenceAtOrAfterHour: 12` is dropped);
 *   • night finishes before 8.00am, not 7.30am — "commences after 6:00pm and
 *     finishing after midnight and before 8.00am".
 * This is the value seeded (as data) onto the Nurses EA `pay_rule_version` and
 * fed to the engine via `RateContext.shiftCharacterRules`; the engine never
 * hardcodes it per-agreement. Non-EA MA000034 keeps `NURSES_SHIFT_RULES`.
 */
export const NURSES_EA_CL22_1_SHIFT_RULES: ShiftCharacterRules = {
  afternoon: {
    // No commenceAtOrAfterHour — afternoon = a shift FINISHING 6pm–midnight.
    finishAfterHour: 18,
    finishAtOrBeforeHour: 24,
  },
  night: {
    commenceAtOrAfterHour: 18, // "commences after 6:00pm"
    finishAfterHour: 0, // "finishing after midnight"
    finishAtOrBeforeHour: 8, // "before 8.00am"
    finishStrictlyBefore: true,
  },
};

// ═══════════════════════════════════════════════════════════════════════
// Recall-to-work rules (cl 19.6 in-person / cl 19.7 electronic)
// ═══════════════════════════════════════════════════════════════════════

/**
 * Phase 1C + Phase 1J Nurses recall-to-work rules. MA000034 cl 19.6
 * (in-person 3h minimum) + cl 19.7 (electronic 1h minimum).
 *
 *   - 3h minimum @ tier-1 OT for in-person recall (cl 19.6).
 *   - 1h minimum @ tier-1 OT for electronic recall (cl 19.7).
 *   - Travel time counts as time worked under cl 19.7(c) only (when NOT
 *     on-call). When on-call (cl 19.6 path), travel-counts does NOT apply.
 *   - Subsequent electronic work past minimum rounds up to 15-min
 *     increments (cl 19.7(b)).
 *   - Within 3h of rostered start → travel-time-only carveout
 *     (cl 19.7(d) only — cl 19.6 has no equivalent).
 *
 * Phase 1J (Award Engine Integrity 2026-05-16) — casual loading is
 * MULTIPLICATIVE on the OT multiplier per cl 19.2(a) (e.g. casual
 * tier-1 = 1.5 × 1.25 = 1.875×). Pre-Phase-1J was ADDITIVE (1.5 + 0.25
 * = 1.75×) — under-paid casual recalls by ~0.125× of base per hour.
 *
 * Phase 1J MD-A8 — hardcoded OT rates per cl 19.2(a). Pre-Phase-1J the
 * engine read SUNDAY (ordinary 1.75) and PUBLIC_HOLIDAY (ordinary 2.0
 * post-Phase-1J seed; pre-Phase-1J 2.5) bands for recall, which is
 * wrong because cl 19.2 OT rates are distinct from cl 21/28 ordinary
 * rates. Sourcing OT rates directly here avoids the band-collapse
 * defect.
 *
 * Phase 1J MD-A4 + MD-A5 — Nurses-specific cl 19.6 vs cl 19.7 split.
 * `onCallScopeGating: true` activates the wasOnCall input gate.
 *
 * Source: FWC consolidated MA000034 cl 19.6 + cl 19.7 + cl 19.2(a) +
 *         FWO library K700647 + PR791118 ppc 1 Oct 2025.
 * Verified: Phase 1C + Phase 1J (2026-05-16) FIX SHAPE specialist.
 * Artifact: docs/operations/clause-verifications/2026-05-16-aged-care-legislation-nurses-math-defect-fix-shape.md
 * Errata: #13 / #14 / #15 (cl 11.4 misreading + Cluster A PH multiplier sub-cite drift).
 */
/**
 * Phase Codex Pass A PR #B (2026-05-19) — Nurses MA000034 OT rate
 * bands for banded recall pay per cl 19.1 + cl 19.2.
 *
 * Verbatim cl 19.1 / cl 19.2 (FWC consolidated MA000034, specialist
 * primary-source verification 2026-05-19):
 *
 *   "19.1 An employee will be paid overtime in accordance with this
 *    clause for all hours worked:
 *    (a) outside the ordinary working hours of the employee;
 *    or (b) in excess of the ordinary working hours of the employee;
 *    or (c) in excess of the maximum number of weekly hours of an
 *           employee."
 *
 *   "19.2 Rates of pay for overtime:
 *    (a) For an employee not specifically classified in clauses 19.4
 *        or 19.5 (i.e. ordinary cl 19 path covering RN/EN/AIN):
 *        (i)   Mon–Sat — first 2 hours: 150%, after 2 hours: 200%;
 *        (ii)  Sunday: 200%;
 *        (iii) Public holiday: 250%."
 *
 * Saturday joins the Mon-Fri tier curve (cl 19.1(a)(i) "Mon-Sat" wording).
 * NO Saturday flat carveout (contrast AC cl 25.1(a)(i)(B)). DO NOT
 * port the AC Saturday-flat shape onto Nurses.
 *
 * Casual loading is MULTIPLICATIVE per cl 19.2(a) wording "X% of the
 * casual hourly rate". The casual hourly rate already incorporates the
 * 25% loading per cl 11.4, so casual tier-1 = 1.5 × 1.25 = 1.875, etc.
 * See project_nurses_casual_loading_model.md for the per-clause model.
 *
 * Source: FWC consolidated MA000034 cl 19.1 + cl 19.2 + FWO library K700647 +
 *         PR791118 ppc 1 Oct 2025.
 * Verified: 2026-05-19 (Phase Codex Pass A PR #B specialist invocation;
 *           statura-regulatory-source-agent autonomous mode).
 * Artifact: docs/operations/clause-verifications/2026-05-19-statura-regulatory-source-recall-banded-ot.md
 */
export const NURSES_OT_RATE_BANDS: OtRateBands = {
  weekdayTier1Hours: 2, // cl 19.2(a)(i) "first 2 hours"
  weekdayTier1: {
    permanent: 1.5, // cl 19.2(a)(i) Mon-Sat first 2h — 150%
    casual: 1.875, // cl 19.2(a)(i) "% of casual hourly rate" — 1.5 × 1.25
  },
  weekdayTier2: {
    permanent: 2.0, // cl 19.2(a)(i) Mon-Sat after 2h — 200%
    casual: 2.5, // 2.0 × 1.25
  },
  // Saturday NOT flat — joins Mon-Fri tier curve per cl 19.1 + cl 19.2(a)(i).
  // saturdayFlat is intentionally omitted; do NOT port AC cl 25.1(a)(i)(B).
  sunday: {
    permanent: 2.0, // cl 19.2(a)(ii) Sunday — 200%
    casual: 2.5, // 2.0 × 1.25
  },
  publicHoliday: {
    permanent: 2.5, // cl 19.2(a)(iii) PH — 250%
    casual: 3.125, // 2.5 × 1.25
  },
  clauseRefs: {
    weekdayTier1:
      'Nurses Award MA000034 cl 19.6/19.7 recall trigger + cl 19.2(a)(i) (Mon-Sat OT first 2 hours — 150% of base or 187.5% of casual base)',
    weekdayTier2:
      'Nurses Award MA000034 cl 19.6/19.7 recall trigger + cl 19.2(a)(i) (Mon-Sat OT after 2 hours — 200% of base or 250% of casual base)',
    sunday:
      'Nurses Award MA000034 cl 19.6/19.7 recall trigger + cl 19.2(a)(ii) (Sunday OT flat — 200% of base or 250% of casual base)',
    publicHoliday:
      'Nurses Award MA000034 cl 19.6/19.7 recall trigger + cl 19.2(a)(iii) (PH OT flat — 250% of base or 312.5% of casual base)',
  },
};

export const NURSES_RECALL_RULES: RecallRules = {
  awardCode: 'MA000034',
  inPersonMinimumHours: 3,
  electronicMinimumHours: 1,
  travelCountsAsTimeWorked: true,
  electronicRoundingMinutes: 15,
  withinRosteredStartCarveoutHours: 3,
  casualLoadingTreatment: 'MULTIPLICATIVE',
  otBands: NURSES_OT_RATE_BANDS,
  onCallScopeGating: true,
  clauseRef:
    'Nurses Award MA000034 cl 19.6 (on-call recall) / cl 19.7 (not-on-call recall) per FWO library K700647 read with cl 19.1 + cl 19.2(a) banded OT rates',
};

// ═══════════════════════════════════════════════════════════════════════
// Classification streams
// ═══════════════════════════════════════════════════════════════════════

/** Classification streams used by the Nurses engine. */
export const NURSES_STREAMS: ReadonlySet<string> = new Set([
  'nurse',
  'aged_care_nurse',
  // Tenant EA overlays (e.g. Medea Park MA000034 Nurses EA) store rate lines
  // under a dedicated stream key while still using NursesEngine.
  'NURSES_EA',
]);

// ═══════════════════════════════════════════════════════════════════════
// PH OT multiplier (cl 19.1(a)(iii))
// ═══════════════════════════════════════════════════════════════════════

/**
 * Public Holiday Overtime multiplier — cl 19.1(a)(iii) = 250% (2.5×).
 *
 * Phase 1J MD-A7 — separated from PH ordinary multiplier (cl 28.2(a) = 200%).
 * Pre-Phase-1J the engine collapsed both onto a single `phMult = 2.5`
 * constant, which was wrong on TWO counts: (1) cl 28.2(a) PH ordinary is
 * 200% not 250%, and (2) the OT rate at cl 19.1(a)(iii) is distinct from
 * the ordinary rate. DB migration 20267077230000 reseeds PUBLIC_HOLIDAY
 * band to 2.0 (ordinary); this constant is used directly for the OT path.
 *
 * Source: FWC consolidated MA000034 cl 19.1(a)(iii) + cl 28.2(a).
 * Verified: Phase 1J (2026-05-16) FIX SHAPE specialist.
 */
export const NURSES_PH_OT_MULT = 2.5;

// ═══════════════════════════════════════════════════════════════════════
// Meal-OT emission semantics (cl 17.3(b)) — Phase 1O
// ═══════════════════════════════════════════════════════════════════════

/**
 * Phase 1O (Award Engine Integrity 2026-05-17) — Nurses Award MA000034
 * meal-OT allowance emission semantics per cl 17.3(b).
 *
 * Verbatim cl 17.3(b) (FWC consolidated MA000034, primary-source verified
 * W22 specialist invocation 2026-05-17 — NSWNMA-republished PDF dated
 * 2025-10-01, last variation PR786738 ppc 01Jul25):
 *
 *     "(b) Meal allowances
 *      (i) When required to work overtime after the usual finishing hour
 *          of work beyond one hour or, in the case of shiftworkers, when
 *          the overtime work on any shift exceeds one hour, an employee
 *          will be:
 *          • supplied with an adequate meal where an employer has
 *            adequate cooking and dining facilities; or
 *          • paid a meal allowance of $16.62.
 *      (ii) In addition to the allowance provided for in clause 17.3(b)(i),
 *           where overtime work exceeds 4 hours a further meal allowance
 *           of $14.98 will be paid.
 *      (iii) Clauses 17.3(b)(i) and 17.3(b)(ii) will not apply when an
 *            employee could reasonably return home for a meal within the
 *            meal break."
 *
 * Pre-Phase-1O the engine emitted ONE MEAL_ALLOWANCE under cl 17.3(b),
 * triggered by the caller `hasDelayedMealAllowance` boolean — silently
 * dropping the cl 17.3(b)(ii) further-meal allowance ($14.98 per OT > 4h
 * event). Direction: under-pay. FW Act s.327A wage-theft criminal regime
 * exposure CONDITIONAL on any nurse OT > 4h shift hitting the engine
 * before Phase 1O. W22 evidence pack at
 * `docs/operations/clause-verifications/2026-05-17-aged-care-legislation-nurses-cl-17-3-b-meal-ot.md`.
 *
 * **Cross-award native symmetry (binding per `project_cross_award_shape_porting_risk.md`)**:
 * MA000034 cl 17.3(b) is STRUCTURALLY IDENTICAL to MA000018 cl 15.4 and
 * MA000027 cl 23.3(e) (same template, same triggers, same dollar amounts —
 * variation determination cycle PR786738/PR786721/PR786730 all ppc 01Jul25).
 * Despite this symmetry, encode the MA000034 constants INDEPENDENTLY here —
 * DO NOT factor `MealOtRules` into `_shared.ts`. The shape-porting binding
 * rule fires in BOTH directions (5th/6th-instance pattern surfaced W22):
 * over-applying one award's shape OR under-applying another's both fail
 * if the structs are factored together and the FWC later diverges them.
 *
 * **Engine derivability**: `overtimeHours > 1.0` is a close approximation
 * of "more than one hour past the usual finishing time" for single-extended-
 * shift OT. Phase 1O moves the meal emission OUT of the OT-unaware
 * `calculate()` path (where it previously fired on the caller flag) and
 * INTO the OT-aware `calculateWithOvertime()` path — mirroring the Aged
 * Care W4 implementation. The two carve-out flags (`mealProvidedDuringOT`,
 * `couldReturnHomeForMeal`) are the rare exception suppressors per
 * cl 17.3(b)(iii); UI capture surface for these flags is W22-A.1
 * post-Madea (engine accepts the flag, defaults to false → no behavior
 * change for current consumers).
 *
 * Source: FWC consolidated MA000034 cl 17.3(b) (NSWNMA republication
 * 2025-10-01 + cross-verified `https://awards.fairwork.gov.au/MA000034.html`).
 * Pay Guide G01002984 (FWO 1 Oct 2025 republication). Amounts pinned via
 * DB rate-registry seed `supabase/migrations/20260603100000_seed_nurses_v1.sql`
 * (`MEAL` PER_INCIDENT $16.62 cl 17.3(b)(i); `MEAL_OT_ADDITIONAL` PER_INCIDENT
 * $14.98 cl 17.3(b)(ii)) — both seeded pre-Phase-1O, engine reads via
 * `findAllowance(ctx.allowances, ..., employment)`.
 * Verified: 2026-05-17 (W22 aged-care-legislation specialist).
 * Artifact: docs/operations/clause-verifications/2026-05-17-aged-care-legislation-nurses-cl-17-3-b-meal-ot.md
 */
export const NURSES_MEAL_OT_EMISSION = {
  awardCode: 'MA000034' as const,
  /** DB lookup key for first meal $16.62 (PER_INCIDENT basis). */
  firstMealAllowanceCode: 'MEAL' as const,
  firstMealAmount: 16.62 as const,
  /** OT must exceed this many hours past usual finish for first meal to fire. */
  firstMealThresholdHours: 1.0 as const,
  /** DB lookup key for additional meal $14.98 (PER_INCIDENT basis). */
  secondMealAllowanceCode: 'MEAL_OT_ADDITIONAL' as const,
  secondMealAmount: 14.98 as const,
  /** Cumulative OT must exceed this many hours for second meal to fire (in addition to first). */
  secondMealThresholdHours: 4.0 as const,
  /** CUMULATIVE semantics: second meal is ADDED to first, not exclusive. */
  cumulative: true as const,
  /** NOT all-purpose — flat emission; casual loading does NOT compound. */
  appliesToCasualLoading: false as const,
  clauseRefFirst:
    'Nurses Award MA000034 cl 17.3(b)(i) (meal allowance — OT > 1h past usual finish; supplied-meal carve-out applies)',
  clauseRefSecond:
    'Nurses Award MA000034 cl 17.3(b)(ii) (further meal allowance — OT > 4h cumulative; additive to first meal)',
};

// ═══════════════════════════════════════════════════════════════════════
// Leave entitlements (NES + MA000034)
// ═══════════════════════════════════════════════════════════════════════

/**
 * Annual leave loading rate for Nurses Award MA000034.
 *
 * Phase 3 PR #3 sub-phase 3G (2026-05-18) — engine emission of
 * LEAVE_LOADING_ALLOWANCE on paid_annual_leave segments for non-casual
 * employees. v1 naive 17.5% × ordinary leave pay only; cl 22.5(b)
 * shiftworker higher-of test (compare 17.5% loading vs penalty rates the
 * worker would have earned) DEFERRED post-Madea per Phase 3 PR #3 scope
 * (W35).
 *
 * Casuals are excluded — NES s.86 + cl 22.5 (annual leave is a permanent
 * entitlement; casuals receive 25% loading on ordinary hours in lieu).
 *
 * Source: FWC consolidated MA000034 cl 22.5 (annual leave loading);
 * NES s.86 (annual leave applies only to non-casual employees).
 * Verified: 2026-05-19 (Codex Pass A retrospective P2-N specialist
 * primary-source check; pre-fix cited cl 33.4(a) which does not exist
 * as annual leave loading in MA000034. PayCat MA000034 summary,
 * Williamson Barwick Nurses Award, FWO Library MA000034 confirm cl 22.5).
 */
export const NURSES_LEAVE = {
  /** 17.5% loading on paid annual leave ordinary hours. */
  annualLeaveLoading: 0.175,
  /** cl 22.5(a) — Nurses Award MA000034. */
  clauseRef:
    'Nurses Award MA000034 cl 22.5(a) (annual leave loading — 17.5% on ordinary leave pay; cl 22.5(b) shiftworker higher-of applied by pay-run leave snapshot from roster evidence)',
} as const;
